import { useAppUser } from '@bechtel/client-common';
import { useDashboardPermissions } from '@bechtel/client-dashboard-common';
import { CrewMember, OwnershipLevel, OwnershipType, PersonaType, WorkPackageStats } from '@bechtel/types';
import { find } from 'lodash';
import { FC, Fragment, useEffect, useState } from 'react';
import { FieldEngineerDashboardContent } from '~/field-engineer';
import { ForemanDashboardContent } from '~/foreman';
import { useDashboardStatsQuery } from '~/hooks';
import { DashboardProgress } from '~/progress';
import { SuperIntendentDashboardContent } from '~/superintendent';
import { dashBoardStatsMock } from '../form-data';

export interface DashboardContentProps {
  selectedCrewMember: CrewMember | null;
  selectedOwnershipLevel: OwnershipLevel;
}

export const DashboardContent: FC<DashboardContentProps> = ({ selectedCrewMember, selectedOwnershipLevel }) => {
  const { user } = useAppUser();
  const [getSelectedCrewMemberDashboardStats, result] = useDashboardStatsQuery();
  const { called, loading, data } = result;
  const [dashboardStats, setdashboardStats] = useState<WorkPackageStats>(dashBoardStatsMock);
  const crewMember = selectedCrewMember ? selectedCrewMember : user?.crewMember;
  const currentProjectWeek = find(user?.project?.projectConfiguration?.projectWeeks, (projectWeek) =>
    projectWeek?.isCurrent ? true : false
  );
  useEffect(() => {
    if (
      !user ||
      !crewMember ||
      !crewMember.id ||
      !crewMember.persona ||
      crewMember?.persona === PersonaType.SUPER_ADMIN
    )
      return;

    getSelectedCrewMemberDashboardStats({
      variables: {
        input: {
          crewMemberId: crewMember.id,
          personaType: crewMember.persona,
          ownershipLevel: selectedOwnershipLevel,
          startDate: currentProjectWeek?.startDate,
          endDate: currentProjectWeek?.endDate,
        },
      },
    });
  }, [selectedOwnershipLevel, user, crewMember, getSelectedCrewMemberDashboardStats]);

  useEffect(() => {
    data && data.workPackageStats
      ? setdashboardStats(data.workPackageStats)
      : setdashboardStats({ ...dashBoardStatsMock });
  }, [data]);

  const {
    canViewSuperIntendentDashboard,
    canViewForemanDashboard,
    canViewSuperAdminDashboard,
    canViewFieldEngineerDashboard,
  } = useDashboardPermissions();

  const { progress, plannedMilestones } = dashboardStats;
  const progressValues = {
    ...progress,
    targetHours: progress.committedHours > progress.approvedHours ? progress.committedHours : progress.approvedHours,
  };

  // This ownerShip type is requred to fetch the WP-3WLA list as in WP query we use OwnershipType instead of OwnershipLevel
  const selectedOwnershipType = selectedOwnershipLevel === OwnershipLevel.PRIMARY ? OwnershipType.PRIMARY : OwnershipType.SECONDARY

  return (
    <Fragment>
      <DashboardProgress
        progress={
          crewMember && crewMember.persona && crewMember.persona === PersonaType.FIELD_ENGINEER
            ? progressValues
            : progress
        }
        showTargetIndicator={
          !!(
            crewMember &&
            crewMember.persona &&
            crewMember.persona !== PersonaType.FIELD_ENGINEER &&
            called &&
            !loading
          )
        }
        crewMember={crewMember}
        plannedMilestones={plannedMilestones}
      />
      {selectedCrewMember &&
        (selectedCrewMember.persona === PersonaType.SUPERINTENDENT ||
          selectedCrewMember.persona === PersonaType.GENERAL_FOREMAN) && (
          <SuperIntendentDashboardContent crewMember={selectedCrewMember} result={result} selectedOwnershipType={selectedOwnershipType} />
        )}
      {selectedCrewMember && selectedCrewMember.persona === PersonaType.FOREMAN && (
        <ForemanDashboardContent crewMember={selectedCrewMember} result={result} selectedOwnershipType={selectedOwnershipType} />
      )}
      {selectedCrewMember && selectedCrewMember.persona === PersonaType.FIELD_ENGINEER && (
        <FieldEngineerDashboardContent crewMember={selectedCrewMember} result={result} selectedOwnershipType={selectedOwnershipType} />
      )}

      {!selectedCrewMember &&
        (((canViewSuperIntendentDashboard || canViewSuperAdminDashboard) && (
          <SuperIntendentDashboardContent crewMember={user?.crewMember} result={result} selectedOwnershipType={selectedOwnershipType}/>
        )) ||
          (canViewForemanDashboard && <ForemanDashboardContent crewMember={user?.crewMember} result={result} selectedOwnershipType={selectedOwnershipType} />) ||
          (canViewFieldEngineerDashboard && (
            <FieldEngineerDashboardContent crewMember={user?.crewMember} result={result} selectedOwnershipType={selectedOwnershipType} />
          )))}
    </Fragment>
  );
};
